// canvasSymbols.tsx — Pure SVG symbol components for PanelCanvas2D
// Extracted from PanelCanvas2D.tsx to enable unit testing and reduce that file's LOC.
// Each component is presentational only — no store access, no refs, no effects.

const FONT_MONO = "'Courier New',monospace";

// Horizontal dimension line — tick marks at x1/x2, line between, label above centred
export function HorizDim({ x1, x2, y, label }: { x1: number; x2: number; y: number; label: string }) {
  if (x2 - x1 < 2) return null;
  const cx = (x1 + x2) / 2;
  const tk = 6;
  return (
    <g>
      <line x1={x1} y1={y - tk} x2={x1} y2={y + tk} stroke="#444" strokeWidth="0.9" />
      <line x1={x2} y1={y - tk} x2={x2} y2={y + tk} stroke="#444" strokeWidth="0.9" />
      <line x1={x1} y1={y}      x2={x2} y2={y}       stroke="#444" strokeWidth="0.9" />
      <line x1={x1}   y1={y} x2={x1+7} y2={y-4} stroke="#444" strokeWidth="0.8"/>
      <line x1={x1}   y1={y} x2={x1+7} y2={y+4} stroke="#444" strokeWidth="0.8"/>
      <line x1={x2}   y1={y} x2={x2-7} y2={y-4} stroke="#444" strokeWidth="0.8"/>
      <line x1={x2}   y1={y} x2={x2-7} y2={y+4} stroke="#444" strokeWidth="0.8"/>
      {(x2 - x1) > 20 && (
        <text x={cx} y={y - 8} textAnchor="middle" fontSize="9" fontFamily={FONT_MONO} fill="#1a1a1a">
          {label}
        </text>
      )}
    </g>
  );
}

// Vertical dimension line — tick marks at y1/y2 (SVG top=y1, SVG bottom=y2), rotated label
export function VertDim({ x, y1, y2, label }: { x: number; y1: number; y2: number; label: string }) {
  const cy = (y1 + y2) / 2;
  const tk = 6;
  return (
    <g>
      <line x1={x - tk} y1={y1} x2={x + tk} y2={y1} stroke="#444" strokeWidth="0.9" />
      <line x1={x - tk} y1={y2} x2={x + tk} y2={y2} stroke="#444" strokeWidth="0.9" />
      <line x1={x}      y1={y1} x2={x}      y2={y2} stroke="#444" strokeWidth="0.9" />
      <line x1={x} y1={y1}   x2={x-4} y2={y1+7} stroke="#444" strokeWidth="0.8"/>
      <line x1={x} y1={y1}   x2={x+4} y2={y1+7} stroke="#444" strokeWidth="0.8"/>
      <line x1={x} y1={y2}   x2={x-4} y2={y2-7} stroke="#444" strokeWidth="0.8"/>
      <line x1={x} y1={y2}   x2={x+4} y2={y2-7} stroke="#444" strokeWidth="0.8"/>
      {(Math.abs(y2 - y1)) > 20 && (
        <text
          x={x + 14} y={cy}
          textAnchor="middle"
          fontSize="9" fontFamily={FONT_MONO} fill="#1a1a1a"
          transform={`rotate(-90,${x + 14},${cy})`}
        >
          {label}
        </text>
      )}
    </g>
  );
}

// ⊠ gold square — weld/embed connection
export function WeldSymbol({ sx, sy, mark }: { sx: number; sy: number; mark: string }) {
  const s = 7;
  return (
    <g>
      <rect x={sx - s} y={sy - s} width={s * 2} height={s * 2} fill="#c8a820" stroke="#5a4a10" strokeWidth="0.9" />
      <line x1={sx - s + 2} y1={sy - s + 2} x2={sx + s - 2} y2={sy + s - 2} stroke="#3a2a08" strokeWidth="1.2" />
      <line x1={sx + s - 2} y1={sy - s + 2} x2={sx - s + 2} y2={sy + s - 2} stroke="#3a2a08" strokeWidth="1.2" />
      <text x={sx + s + 4} y={sy + 4} fontSize="8" fontFamily={FONT_MONO} fill="#222">{mark}</text>
    </g>
  );
}

// ⊕ orange circle — brace insert
export function BraceSymbol({ sx, sy, mark }: { sx: number; sy: number; mark: string }) {
  const r = 8;
  return (
    <g>
      <circle cx={sx} cy={sy} r={r} fill="none" stroke="#d04800" strokeWidth="1.4" />
      <line x1={sx - r} y1={sy} x2={sx + r} y2={sy} stroke="#d04800" strokeWidth="0.9" />
      <line x1={sx} y1={sy - r} x2={sx} y2={sy + r} stroke="#d04800" strokeWidth="0.9" />
      <text x={sx + r + 4} y={sy + 4} fontSize="8" fontFamily={FONT_MONO} fill="#222">{mark}</text>
    </g>
  );
}

// △ green triangle — pick point (lift anchor)
export function PickSymbol({ sx, sy, mark }: { sx: number; sy: number; mark: string }) {
  const s = 9;
  const pts = `${sx},${sy - s} ${sx - s},${sy + s * 0.6} ${sx + s},${sy + s * 0.6}`;
  return (
    <g>
      <polygon points={pts} fill="rgba(0,160,48,0.12)" stroke="#00a030" strokeWidth="1.4" />
      <text x={sx + s + 4} y={sy + 4} fontSize="8" fontFamily={FONT_MONO} fill="#222">{mark}</text>
    </g>
  );
}

// Crosshair-in-circle — center of gravity marker
export function CGMarker({ cx, cy, label }: { cx: number; cy: number; label: string }) {
  const arm = 16;
  const r = 7;
  return (
    <g>
      <line x1={cx - arm} y1={cy} x2={cx + arm} y2={cy} stroke="#1a6ca8" strokeWidth="1.1" />
      <line x1={cx} y1={cy - arm} x2={cx} y2={cy + arm} stroke="#1a6ca8" strokeWidth="1.1" />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#1a6ca8" strokeWidth="1.2" />
      <text x={cx + arm + 4} y={cy - 3}  fontSize="8" fontFamily={FONT_MONO} fill="#1a6ca8">{label}</text>
    </g>
  );
}
